import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Settings, Loader2, AlertCircle } from "lucide-react";
import { AppShell, PageHeader } from "@/components/app";
import { QuotaDashboard } from "@/components/QuotaDashboard";
import { Survey } from "@/types/survey";

export default function QuotaManagementPage() {
  const { id } = useParams<{ id: string }>();
  const surveyId = parseInt(id || "0");

  // Fetch survey to get title and quotasEnabled status
  const { data: survey, isLoading, error } = useQuery<Survey>({
    queryKey: [`/api/surveys/${surveyId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!surveyId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading quota data...</p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Survey Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The survey you're looking for doesn't exist or you don't have access to it.
            </p>
            <Link to="/projects">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Projects
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppShell maxWidth="wide">
      <PageHeader
        title="Quota Management"
        subtitle={`Track and manage response quotas for ${survey.title}`}
        breadcrumbs={[
          { label: "Projects", href: "/projects" },
          { label: survey.title, href: `/survey/${surveyId}` },
          { label: "Quota Management" }
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/survey/${surveyId}/responses`}>
              <Button variant="outline" size="sm" aria-label="Back to responses">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Responses
              </Button>
            </Link>
            <Link to={`/survey/${surveyId}`}>
              <Button variant="outline" size="sm" aria-label="Configure survey settings">
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </Link>
          </div>
        }
      />
      <QuotaDashboard
        surveyId={surveyId}
        quotasEnabled={survey.settings?.quotasEnabled}
      />
    </AppShell>
  );
}
